import { useState, useMemo, useEffect } from "react";
import type { SlotAvailability } from "../lib/types";
import { RESOURCE_LABELS, DAY_NAMES } from "../lib/types";
import { checkDiscountCode, createMultiBooking, redirectToCheckout } from "../lib/hooks";
import { bookedPrices, creditToUse, type AppliedDiscount } from "../lib/discount";
import { Modal } from "./Modal";

interface BookingPopupProps {
  slot: SlotAvailability;
  siblingSlots?: SlotAvailability[];
  isLoggedIn: boolean;
  creditBalance?: number;
  onClose: () => void;
  onBooked: (summary: string, customerCode?: string) => void;
  onLoginPrompt: () => void;
}

export function BookingPopup({ slot, siblingSlots = [], isLoggedIn, creditBalance = 0, onClose, onBooked, onLoginPrompt }: BookingPopupProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedEndTime, setSelectedEndTime] = useState(slot.end_time.slice(0, 5));
  const [numPersons, setNumPersons] = useState(1);
  const [codeOpen, setCodeOpen] = useState(false);
  const [codeInput, setCodeInput] = useState("");
  const [codeChecking, setCodeChecking] = useState(false);
  const [codeError, setCodeError] = useState("");
  const [discount, setDiscount] = useState<AppliedDiscount | null>(null);

  const date = new Date(slot.date + "T00:00:00");
  const dayName = DAY_NAMES[date.getDay()];
  const dateStr = date.toLocaleDateString("sk-SK", {
    day: "numeric",
    month: "numeric",
    year: "numeric",
  });
  const dateShort = date.toLocaleDateString("sk-SK", { day: "numeric", month: "numeric" });
  const timeStart = slot.start_time.slice(0, 5);
  const label = slot.title || RESOURCE_LABELS[slot.resource_type];
  const isFull = slot.available <= 0;
  const isWorkshop = slot.resource_type === "workshop";

  // Build end-time options: consecutive slots with enough capacity for numPersons
  const endTimeOptions = useMemo(() => {
    if (isWorkshop || siblingSlots.length === 0) {
      return [{ endTime: slot.end_time.slice(0, 5), slots: [slot] }];
    }

    const sorted = [...siblingSlots].sort((a, b) => a.start_time.localeCompare(b.start_time));
    const startIdx = sorted.findIndex((s) => s.slot_id === slot.slot_id);
    if (startIdx === -1) return [{ endTime: slot.end_time.slice(0, 5), slots: [slot] }];

    const options: { endTime: string; slots: SlotAvailability[] }[] = [];
    const collected: SlotAvailability[] = [];

    for (let i = startIdx; i < sorted.length; i++) {
      const s = sorted[i];
      // Must be consecutive
      if (i > startIdx) {
        const prevEnd = sorted[i - 1].end_time.slice(0, 5);
        const curStart = s.start_time.slice(0, 5);
        if (curStart !== prevEnd) break;
      }
      // Must have enough capacity for numPersons
      if (s.available < numPersons) break;

      collected.push(s);
      options.push({
        endTime: s.end_time.slice(0, 5),
        slots: [...collected],
      });
    }

    if (options.length === 0) {
      return [{ endTime: slot.end_time.slice(0, 5), slots: [slot] }];
    }

    return options;
  }, [slot, siblingSlots, isWorkshop, numPersons]);

  // Clamp selectedEndTime if it's no longer valid
  useEffect(() => {
    if (!endTimeOptions.find((o) => o.endTime === selectedEndTime)) {
      setSelectedEndTime(endTimeOptions[endTimeOptions.length - 1]?.endTime ?? slot.end_time.slice(0, 5));
    }
  }, [endTimeOptions, selectedEndTime, slot.end_time]);

  // Find the selected option
  const selectedOption = endTimeOptions.find((o) => o.endTime === selectedEndTime) ?? endTimeOptions[0];
  const selectedSlots = selectedOption.slots;
  const hourCount = selectedSlots.length;

  // Max persons = min available across all selected slots
  const maxPersons = useMemo(() => {
    return Math.max(1, Math.min(...selectedSlots.map((s) => s.available)));
  }, [selectedSlots]);

  // Clamp numPersons if maxPersons decreased
  useEffect(() => {
    if (numPersons > maxPersons) {
      setNumPersons(maxPersons);
    }
  }, [maxPersons, numPersons]);

  // Price: hours × persons; the discount is priced per hour exactly like the API does
  // (a percentage off every hour, a fixed amount off the first one).
  const totalPriceCents = selectedSlots.reduce((sum, s) => sum + s.price_cents, 0) * numPersons;
  const totalPrice = (totalPriceCents / 100).toFixed(2).replace(".", ",");
  const discountedCents = bookedPrices(selectedSlots.map((s) => s.price_cents * numPersons), discount)
    .reduce((sum, cents) => sum + cents, 0);
  const discountCents = totalPriceCents - discountedCents;

  const handleApplyCode = async () => {
    const code = codeInput.trim();
    if (!code) return;
    setCodeChecking(true);
    setCodeError("");
    const { data, error: err } = await checkDiscountCode(code);
    setCodeChecking(false);
    if (err || !data) {
      setCodeError(err?.message ?? "Kód sa nepodarilo overiť.");
      return;
    }
    setDiscount(data);
    setCodeInput("");
    setCodeOpen(false);
  };

  const handleRemoveCode = () => {
    setDiscount(null);
    setError("");
  };

  const handleBook = async () => {
    setLoading(true);
    setError("");
    const slotIds = selectedSlots.map((s) => s.slot_id);
    const { data, error: err } = await createMultiBooking(slotIds, numPersons, discount?.code);
    if (err) {
      setError(err.message);
      setLoading(false);
    } else {
      // If Stripe checkout URL returned, redirect to payment page
      if (data?.checkout_url) {
        redirectToCheckout(data.checkout_url);
        return;
      }
      const personsSuffix = numPersons > 1 ? `, ${numPersons} osoby` : "";
      const summary = `${dayName} ${dateShort}, ${timeStart} – ${selectedEndTime}, ${label} (${hourCount}h${personsSuffix})`;
      const customerCode = data?.customer_code;
      onBooked(summary, customerCode);
    }
  };

  // Build price breakdown text
  const priceBreakdown = (() => {
    const parts: string[] = [];
    if (numPersons > 1) parts.push(`${numPersons} os.`);
    if (hourCount > 1) parts.push(`${hourCount}h`);
    parts.push(`${(slot.price_cents / 100).toFixed(0)} €`);
    return parts.length > 1 ? parts.join(" × ") : "";
  })();

  const fmtEur = (cents: number) => (cents / 100).toFixed(2).replace(".", ",") + " €";
  const creditUse = creditToUse(discountedCents, creditBalance);
  const amountDue = discountedCents - creditUse;
  const occupancyPct = slot.capacity > 0 ? Math.round((slot.booked / slot.capacity) * 100) : 0;

  return (
    <Modal>
      <div className="popup-overlay" onClick={onClose}>
        <div className="popup" onClick={(e) => e.stopPropagation()}>
          <div className="popup-head">
            <span className="popup-eyebrow">{isWorkshop ? "Workshop" : "Rezervácia"}</span>
            <h3>{label}</h3>
            <span className="popup-sub">{dayName}, {dateStr}</span>
          </div>

          <dl className="popup-grid">
            <dt>Čas</dt>
            <dd>
              {timeStart} –{" "}
              {!isWorkshop && endTimeOptions.length > 1 ? (
                <select
                  className="time-select"
                  value={selectedEndTime}
                  onChange={(e) => setSelectedEndTime(e.target.value)}
                >
                  {endTimeOptions.map((opt) => (
                    <option key={opt.endTime} value={opt.endTime}>
                      {opt.endTime} ({opt.slots.length}h)
                    </option>
                  ))}
                </select>
              ) : (
                <span>{selectedEndTime}</span>
              )}
            </dd>

            {slot.clayground && (
              <>
                <dt>Clayground</dt>
                <dd className="popup-clayground">V štúdiu bude lektorka, poradí a pomôže.</dd>
              </>
            )}

            {!isFull && (
              <>
                <dt>Osoby</dt>
                <dd>
                  {maxPersons > 1 ? (
                    <select
                      className="person-select"
                      value={numPersons}
                      onChange={(e) => setNumPersons(Number(e.target.value))}
                    >
                      {Array.from({ length: maxPersons }, (_, i) => i + 1).map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  ) : (
                    <span>1</span>
                  )}
                </dd>
              </>
            )}

            <dt>Obsadenosť</dt>
            <dd className="popup-occupancy">
              <span className="popup-bar" aria-hidden="true">
                <span className="popup-bar-fill" style={{ width: `${occupancyPct}%` }}></span>
              </span>
              <span>{isFull ? "obsadené" : `${slot.booked} z ${slot.capacity}`}</span>
            </dd>
          </dl>

          {!isFull && (
            <dl className="popup-grid popup-sum">
              <dt>Cena</dt>
              <dd className="popup-total">
                {totalPrice} €
                {priceBreakdown && <span className="price-detail">{priceBreakdown}</span>}
              </dd>
              {discount && (
                <>
                  <dt>Zľava</dt>
                  <dd className="popup-discount">
                    −{fmtEur(discountCents)}
                    <span className="price-detail">
                      {discount.code} {discount.amount_cents ? `−${fmtEur(discount.amount_cents)}` : `−${discount.percent} %`}{" "}
                      <button type="button" className="link-btn" onClick={handleRemoveCode} disabled={loading}>
                        zrušiť
                      </button>
                    </span>
                  </dd>
                </>
              )}
              {creditUse > 0 && (
                <>
                  <dt>Z kreditu</dt>
                  <dd className="popup-credit">−{fmtEur(creditUse)}</dd>
                </>
              )}
              {(creditUse > 0 || discount) && (
                <>
                  <dt>K úhrade</dt>
                  <dd className="popup-due">
                    {amountDue > 0
                      ? fmtEur(amountDue)
                      : creditUse > 0
                        ? "0,00 € · plne kryté kreditom"
                        : "0,00 € · zadarmo"}
                  </dd>
                </>
              )}
            </dl>
          )}

          {isLoggedIn && !isFull && !discount && (
            <div className="popup-discount-code">
              {!codeOpen ? (
                <button type="button" className="link-btn" onClick={() => setCodeOpen(true)}>
                  Mám zľavový kód
                </button>
              ) : (
                <form
                  className="popup-discount-row"
                  onSubmit={(e) => { e.preventDefault(); handleApplyCode(); }}
                >
                  <input
                    type="text"
                    value={codeInput}
                    onChange={(e) => { setCodeInput(e.target.value.toUpperCase()); setCodeError(""); }}
                    placeholder="Zľavový kód"
                    aria-label="Zľavový kód"
                    autoComplete="off"
                    autoCapitalize="characters"
                    spellCheck={false}
                    maxLength={32}
                    autoFocus
                    disabled={codeChecking}
                  />
                  <button type="submit" className="btn btn-secondary btn-small" disabled={codeChecking || !codeInput.trim()}>
                    {codeChecking ? "Overujem…" : "Použiť"}
                  </button>
                </form>
              )}
              {codeError && <div className="form-error">{codeError}</div>}
            </div>
          )}

          {isFull && <p className="popup-full">Tento termín je obsadený.</p>}

          {loading && <div className="form-hint">Vytváram PIN kód…</div>}
          {error && <div className="form-error">{error}</div>}

          {!isLoggedIn && !isFull && (
            <div className="popup-login-hint">
              Pre rezerváciu sa prihláste
            </div>
          )}

          <div className="popup-actions">
            <button className="btn btn-secondary" onClick={onClose}>
              Zavrieť
            </button>
            {isLoggedIn && !isFull && (
              <button className="btn btn-primary" onClick={handleBook} disabled={loading}>
                {loading ? "Rezervujem…" : `Rezervovať${numPersons > 1 ? ` ${numPersons} osoby` : ""} →`}
              </button>
            )}
            {!isLoggedIn && !isFull && (
              <button className="btn btn-primary" onClick={() => { onClose(); onLoginPrompt(); }}>
                Prihlásiť sa
              </button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
